import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import renewalsApi from '../api/renewalsApi';
import quotationsApi from '../api/quotationsApi';
import Sidebar from './Sidebar';
import UserDropdown from './UserDropdown';
import { numberError, INT_MAX, DECIMAL_10_2_MAX } from '../utils/fieldLimits';
import {
  Search, X, ChevronLeft, ChevronRight, Eye, RefreshCw, Plus, Trash2, CheckCircle2, XCircle, Clock
} from 'lucide-react';

const STATUS_BADGE = {
  eligible: { bg: '#f0fdf4', color: '#16a34a', label: 'ELIGIBLE' },
  not_eligible: { bg: '#fef2f2', color: '#dc2626', label: 'NOT ELIGIBLE' },
  renewed: { bg: '#eff6ff', color: '#2563eb', label: 'RENEWED' }
};

const SERVICE_STATUS_BADGE = {
  completed: { bg: '#f0fdf4', color: '#16a34a', label: 'COMPLETED' },
  closed: { bg: '#f3f4f6', color: '#6b7280', label: 'CLOSED' },
  ongoing: { bg: '#fef3c7', color: '#d97706', label: 'ACTIVE / ONGOING' }
};

const emptyAddedRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  service_id: '', quantity: 1, rate: 0, tax_id: '', description: ''
});

const Renewal = () => {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedSaleOrderId, setSelectedSaleOrderId] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  // Keyed by sale_order_detail_id -> { included, quantity, rate }. Lets a kept
  // service both be excluded (unchecked) and have its qty/rate amount-edited.
  const [keptState, setKeptState] = useState({});
  const [addedRows, setAddedRows] = useState([]);
  const [dropdownData, setDropdownData] = useState({ services: [], tax_rates: [] });
  const [creating, setCreating] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await renewalsApi.list({ page, limit, search, status: statusFilter });
      if (response.data.success) {
        setRenewals(response.data.data);
        setTotalPages(response.data.pagination.total_pages);
        setTotalItems(response.data.pagination.total_items);
      }
    } catch (error) {
      console.error('Error fetching renewals:', error);
      toast.error('Failed to load renewals');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  useEffect(() => { fetchRenewals(); }, [fetchRenewals]);

  useEffect(() => {
    (async () => {
      try {
        const dropdownsRes = await quotationsApi.getDropdowns();
        if (dropdownsRes.data.success) setDropdownData(dropdownsRes.data.data);
      } catch (error) {
        console.error('Error fetching dropdowns:', error);
      }
    })();
  }, []);

  const handleClearFilters = () => { setSearch(''); setStatusFilter(''); setPage(1); };

  const openDetails = async (saleOrderId) => {
    setSelectedSaleOrderId(saleOrderId);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setAddedRows([]);
    try {
      const response = await renewalsApi.getEligibility(saleOrderId);
      if (response.data.success) {
        const data = response.data.data;
        setEligibility(data);
        const qsByServiceDetailId = {};
        for (const qs of data.original_quotation_details || []) qsByServiceDetailId[qs.id] = qs;
        const initialKept = {};
        for (const s of data.eligibility.completed) {
          const qs = qsByServiceDetailId[s.quotation_service_id];
          initialKept[s.sale_order_detail_id] = {
            included: true,
            quantity: qs ? Number(qs.quantity) : s.qty,
            rate: qs ? Number(qs.rate) : Number(s.rate)
          };
        }
        setKeptState(initialKept);
      }
    } catch (error) {
      console.error('Error fetching renewal eligibility:', error);
      toast.error('Failed to load renewal details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setEligibility(null);
    setSelectedSaleOrderId(null);
    setKeptState({});
    setAddedRows([]);
  };

  const openView = async (renewalId) => {
    setShowViewModal(true);
    setViewLoading(true);
    try {
      const response = await renewalsApi.getQuotationDetails(renewalId);
      if (response.data.success) setViewData(response.data.data);
    } catch (error) {
      console.error('Error fetching renewal quotation details:', error);
      toast.error('Failed to load renewal quotation preview');
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => { setShowViewModal(false); setViewData(null); };

  const toggleKeptIncluded = (sodId) => {
    setKeptState((prev) => ({ ...prev, [sodId]: { ...prev[sodId], included: !prev[sodId]?.included } }));
  };

  const updateKeptField = (sodId, field, value) => {
    setKeptState((prev) => ({ ...prev, [sodId]: { ...prev[sodId], [field]: value } }));
  };

  const addRow = () => setAddedRows((prev) => [...prev, emptyAddedRow()]);
  const removeRow = (key) => setAddedRows((prev) => prev.filter((r) => r.key !== key));
  const updateRow = (key, field, value) => {
    setAddedRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const findTax = (taxId) => dropdownData.tax_rates.find((t) => String(t.tax_id) === String(taxId));

  const computeRowAmounts = (row) => {
    const qty = Number(row.quantity) || 0;
    const rate = Number(row.rate) || 0;
    const amount = Math.round(qty * rate * 100) / 100;
    const tax = findTax(row.tax_id);
    const igstPct = tax ? Number(tax.igst || 0) : 0;
    const igstAmount = Math.round(((amount * igstPct) / 100) * 100) / 100;
    const total = Math.round((amount + igstAmount) * 100) / 100;
    return { amount, igstPct, igstAmount, total };
  };

  // Kept rows' tax % come from the original quotation_service (igst), matched
  // by quotation_service_id — amount/total are recomputed live from the
  // possibly-edited quantity/rate, mirroring the backend's own calculation.
  const qsByServiceDetailId = () => {
    const map = {};
    for (const qs of eligibility?.original_quotation_details || []) map[qs.id] = qs;
    return map;
  };

  const previewRows = () => {
    if (!eligibility) return [];
    const qsMap = qsByServiceDetailId();

    const keptRows = eligibility.eligibility.completed
      .filter((s) => keptState[s.sale_order_detail_id]?.included)
      .map((s) => {
        const state = keptState[s.sale_order_detail_id];
        const qs = qsMap[s.quotation_service_id];
        const quantity = Number(state.quantity) || 0;
        const rate = Number(state.rate) || 0;
        const amount = Math.round(quantity * rate * 100) / 100;
        const igstPct = qs ? Number(qs.igst || 0) : 0;
        const total = Math.round((amount + (amount * igstPct) / 100) * 100) / 100;
        return {
          key: `kept-${s.sale_order_detail_id}`,
          sodId: s.sale_order_detail_id,
          service_name: s.service_name,
          quantity, rate, amount, tax_pct: igstPct, total, isKept: true
        };
      });

    const addedComputed = addedRows.map((row) => {
      const { amount, igstPct, total } = computeRowAmounts(row);
      const service = dropdownData.services.find((s) => String(s.id) === String(row.service_id));
      return {
        key: row.key,
        service_name: service ? service.service_name : '(select service)',
        quantity: row.quantity,
        rate: row.rate,
        amount,
        tax_pct: igstPct,
        total,
        isKept: false
      };
    });

    return [...keptRows, ...addedComputed];
  };

  const grandTotal = () => previewRows().reduce((sum, r) => sum + (Number(r.total) || 0), 0);

  const handleCreateRenewal = async () => {
    if (!eligibility) return;
    const keptEntries = Object.entries(keptState).filter(([, v]) => v.included);
    if (keptEntries.length === 0 && addedRows.length === 0) {
      toast.error('Select at least one service to renew');
      return;
    }
    for (const [sodId, v] of keptEntries) {
      if (!v.quantity || Number(v.quantity) <= 0) {
        toast.error('Quantity must be greater than 0 for all kept services');
        return;
      }
      if (v.rate === '' || v.rate === null || Number(v.rate) < 0) {
        toast.error('Rate cannot be negative for any kept service');
        return;
      }
      const sizeError = numberError(v.quantity, { label: 'Quantity', min: 1, max: INT_MAX, integer: true }) || numberError(v.rate, { label: 'Rate' });
      if (sizeError) {
        toast.error(sizeError);
        return;
      }
    }
    for (const row of addedRows) {
      if (!row.service_id || !row.tax_id || !row.quantity || Number(row.rate) < 0) {
        toast.error('Each added service needs a service, quantity, rate and tax selected');
        return;
      }
      const sizeError = numberError(row.quantity, { label: 'Quantity', min: 1, max: INT_MAX, integer: true }) || numberError(row.rate, { label: 'Rate' });
      if (sizeError) {
        toast.error(sizeError);
        return;
      }
    }

    const totalError = numberError(grandTotal(), { label: 'Grand total' });
    if (totalError) {
      toast.error(`${totalError} - reduce the quantity or rate`);
      return;
    }

    setCreating(true);
    try {
      const payload = {
        source_sale_order_id: selectedSaleOrderId,
        kept_services: keptEntries.map(([sodId, v]) => ({
          sale_order_detail_id: Number(sodId),
          quantity: Number(v.quantity),
          rate: Number(v.rate)
        })),
        added_services: addedRows.map((r) => ({
          service_id: Number(r.service_id),
          quantity: Number(r.quantity),
          rate: Number(r.rate),
          tax_id: Number(r.tax_id),
          description: r.description
        }))
      };
      const response = await renewalsApi.create(payload);
      if (response.data.success) {
        toast.success(response.data.message || 'Renewal created successfully');
        closeDetails();
        fetchRenewals();
      } else {
        toast.error(response.data.message || 'Failed to create renewal');
      }
    } catch (error) {
      console.error('Error creating renewal:', error);
      toast.error(error.response?.data?.message || 'Failed to create renewal');
    } finally {
      setCreating(false);
    }
  };

  const inputStyle = { width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.8rem' };

  return (
    <div className="main-container" style={{ display: 'flex', height: '100vh', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <Sidebar />
      <div className="main-content" style={{ flex: 1, padding: window.innerWidth <= 768 ? '1rem' : '2rem', overflowX: 'auto', height: '100vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>Renewals</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Sale orders ready for renewal, based on completed billing cycles</p>
          </div>
          <UserDropdown />
        </div>

        {/* Filters */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search customer, sale order, quotation..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2.25rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.875rem' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.875rem' }}
          >
            <option value="">All Statuses</option>
            <option value="eligible">Eligible</option>
            <option value="not_eligible">Not Eligible</option>
            <option value="renewed">Renewed</option>
          </select>
          {(search || statusFilter) && (
            <button onClick={handleClearFilters} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem' }}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="crm-sticky-table" style={{ backgroundColor: 'white', borderRadius: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Customer</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Sale Order</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Completed</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Closed</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Ongoing</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Billing Frequencies</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>SO Date</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#6b7280' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
              ) : renewals.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No sale orders found</td></tr>
              ) : (
                renewals.map((row) => {
                  const badge = STATUS_BADGE[row.renewal_status] || STATUS_BADGE.not_eligible;
                  return (
                    <tr key={row.sale_order_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{row.company_name}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{row.saleorder_no}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#16a34a', fontWeight: 600 }}>{row.completed_count}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>{row.closed_count}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: row.ongoing_count > 0 ? '#d97706' : '#6b7280' }}>{row.ongoing_count}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#6b7280' }}>{row.billing_frequencies || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{row.so_date}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 600, backgroundColor: badge.bg, color: badge.color }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {row.renewal_status === 'renewed' ? (
                          <button onClick={() => openView(row.renewal_id)} title="View renewal" style={{ padding: '0.4rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#2563eb' }}>
                            <Eye size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => openDetails(row.sale_order_id)}
                            title={row.renewal_status === 'eligible' ? 'Show details / create renewal' : 'Show details'}
                            style={{ padding: '0.4rem 0.75rem', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', backgroundColor: row.renewal_status === 'eligible' ? '#3b82f6' : '#e5e7eb', color: row.renewal_status === 'eligible' ? 'white' : '#374151' }}
                          >
                            <RefreshCw size={14} /> Show Details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Showing {totalItems === 0 ? 0 : (page - 1) * limit + 1} to {Math.min(page * limit, totalItems)} of {totalItems} results
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}>
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Details / Create Renewal modal */}
        {showDetailsModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002, padding: '1rem' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', maxWidth: '1100px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Renewal Details</h2>
                <button onClick={closeDetails} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={22} /></button>
              </div>

              <div style={{ padding: '1.5rem' }}>
                {detailsLoading || !eligibility ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Loading...</div>
                ) : (
                  <>
                    {/* Customer / quotation / SO summary */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer</div>
                        <div style={{ fontWeight: 600 }}>{eligibility.customer.company_name}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Original Sale Order</div>
                        <div style={{ fontWeight: 600 }}>{eligibility.sale_order.saleorder_no}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Original Quotation</div>
                        <div style={{ fontWeight: 600 }}>{eligibility.original_quotation?.quotation_no}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Eligibility</div>
                        <div style={{ fontWeight: 600, color: eligibility.eligibility.is_eligible ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {eligibility.eligibility.is_eligible ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                          {eligibility.eligibility.is_eligible ? 'Eligible for renewal' : 'Not eligible'}
                        </div>
                      </div>
                    </div>

                    {!eligibility.eligibility.is_eligible && (
                      <div style={{ backgroundColor: '#fef2f2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                        {eligibility.eligibility.ongoing_count > 0
                          ? 'One or more services are still active/ongoing. Renewal is blocked until they complete their billing cycle or are closed.'
                          : 'No completed services are available to renew yet.'}
                      </div>
                    )}

                    {/* Service status table */}
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Services &amp; Billing Period Status</h3>
                    <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>Keep</th>
                            <th style={{ padding: '0.5rem' }}>Service</th>
                            <th style={{ padding: '0.5rem' }}>Billing Frequency</th>
                            <th style={{ padding: '0.5rem' }}>Last Period</th>
                            <th style={{ padding: '0.5rem' }}>PI No.</th>
                            <th style={{ padding: '0.5rem' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eligibility.eligibility.service_statuses.map((s) => {
                            const badge = SERVICE_STATUS_BADGE[s.effective_status];
                            const isCompleted = s.effective_status === 'completed';
                            return (
                              <tr key={s.sale_order_detail_id} style={{ borderBottom: '1px solid #e5e7eb', opacity: isCompleted ? 1 : 0.6 }}>
                                <td style={{ padding: '0.5rem' }}>
                                  {isCompleted ? (
                                    <input type="checkbox" checked={!!keptState[s.sale_order_detail_id]?.included} onChange={() => toggleKeptIncluded(s.sale_order_detail_id)} />
                                  ) : (
                                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>excluded</span>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem' }}>{s.service_name}</td>
                                <td style={{ padding: '0.5rem' }}>{s.cycle_name || '-'}</td>
                                <td style={{ padding: '0.5rem' }}>{s.bill_from_date && s.bill_to_date ? `${s.bill_from_date} to ${s.bill_to_date}` : '-'}</td>
                                <td style={{ padding: '0.5rem' }}>{s.latest_invoice_no || '-'}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  <span style={{ padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 600, backgroundColor: badge.bg, color: badge.color }}>
                                    {badge.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Add service */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Renewal Quotation Preview</h3>
                      <button onClick={addRow} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.75rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                        <Plus size={14} /> Add Service
                      </button>
                    </div>

                    <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>Service</th>
                            <th style={{ padding: '0.5rem' }}>Qty</th>
                            <th style={{ padding: '0.5rem' }}>Rate</th>
                            <th style={{ padding: '0.5rem' }}>Amount</th>
                            <th style={{ padding: '0.5rem' }}>Tax</th>
                            <th style={{ padding: '0.5rem' }}>Total</th>
                            <th style={{ padding: '0.5rem' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows().filter((r) => r.isKept).map((r) => (
                            <tr key={r.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.5rem' }}>{r.service_name}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="number" min="1" max={INT_MAX}
                                  value={keptState[r.sodId]?.quantity ?? ''}
                                  onChange={(e) => updateKeptField(r.sodId, 'quantity', e.target.value)}
                                  style={{ ...inputStyle, width: '70px' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="number" min="0" step="0.01" max={DECIMAL_10_2_MAX}
                                  value={keptState[r.sodId]?.rate ?? ''}
                                  onChange={(e) => updateKeptField(r.sodId, 'rate', e.target.value)}
                                  style={{ ...inputStyle, width: '90px' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>{Number(r.amount).toFixed(2)}</td>
                              <td style={{ padding: '0.5rem' }}>{r.tax_pct}%</td>
                              <td style={{ padding: '0.5rem', fontWeight: 600 }}>{Number(r.total).toFixed(2)}</td>
                              <td style={{ padding: '0.5rem', color: '#9ca3af', fontSize: '0.7rem' }}>from original quotation</td>
                            </tr>
                          ))}
                          {addedRows.map((row) => {
                            const { amount, igstPct, total } = computeRowAmounts(row);
                            return (
                              <tr key={row.key} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                <td style={{ padding: '0.5rem' }}>
                                  <select value={row.service_id} onChange={(e) => updateRow(row.key, 'service_id', e.target.value)} style={inputStyle}>
                                    <option value="">Select Service</option>
                                    {dropdownData.services.map((s) => <option key={s.id} value={s.id}>{s.service_name}</option>)}
                                  </select>
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <input type="number" min="1" max={INT_MAX} value={row.quantity} onChange={(e) => updateRow(row.key, 'quantity', e.target.value)} style={{ ...inputStyle, width: '70px' }} />
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <input type="number" min="0" step="0.01" max={DECIMAL_10_2_MAX} value={row.rate} onChange={(e) => updateRow(row.key, 'rate', e.target.value)} style={{ ...inputStyle, width: '90px' }} />
                                </td>
                                <td style={{ padding: '0.5rem' }}>{amount.toFixed(2)}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  <select value={row.tax_id} onChange={(e) => updateRow(row.key, 'tax_id', e.target.value)} style={inputStyle}>
                                    <option value="">Tax</option>
                                    {dropdownData.tax_rates.map((t) => <option key={t.tax_id} value={t.tax_id}>{t.tax_name}</option>)}
                                  </select>
                                </td>
                                <td style={{ padding: '0.5rem', fontWeight: 600 }}>{total.toFixed(2)}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  <button onClick={() => removeRow(row.key)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626' }}>
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          {previewRows().length === 0 && (
                            <tr><td colSpan={7} style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>No services selected yet</td></tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={5} style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 700 }}>Grand Total</td>
                            <td colSpan={2} style={{ padding: '0.5rem', fontWeight: 700 }}>₹{grandTotal().toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                      <button onClick={closeDetails} style={{ padding: '0.6rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', backgroundColor: 'white', cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateRenewal}
                        disabled={!eligibility.eligibility.is_eligible || creating || (Object.values(keptState).every((v) => !v.included) && addedRows.length === 0)}
                        style={{
                          padding: '0.6rem 1.25rem', border: 'none', borderRadius: '0.5rem', color: 'white', fontWeight: 600,
                          cursor: (!eligibility.eligibility.is_eligible || creating) ? 'not-allowed' : 'pointer',
                          backgroundColor: (!eligibility.eligibility.is_eligible || creating) ? '#9ca3af' : '#16a34a'
                        }}
                      >
                        {creating ? 'Creating...' : 'Create Renewal'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* View already-created renewal modal */}
        {showViewModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002, padding: '1rem' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Renewal Quotation Preview</h2>
                <button onClick={closeView} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={22} /></button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                {viewLoading || !viewData ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Loading...</div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Renewal No.</div><div style={{ fontWeight: 600 }}>{viewData.renewal.renewal_no}</div></div>
                      <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>New Quotation</div><div style={{ fontWeight: 600 }}>{viewData.quotation.quotation_no}</div></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}><Clock size={14} color="#6b7280" /><div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{new Date(viewData.renewal.created_at).toLocaleString()}</div></div>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                          <th style={{ padding: '0.5rem' }}>Service</th>
                          <th style={{ padding: '0.5rem' }}>Qty</th>
                          <th style={{ padding: '0.5rem' }}>Rate</th>
                          <th style={{ padding: '0.5rem' }}>Amount</th>
                          <th style={{ padding: '0.5rem' }}>Tax</th>
                          <th style={{ padding: '0.5rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewData.services.map((s) => (
                          <tr key={s.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.5rem' }}>{s.service_name}</td>
                            <td style={{ padding: '0.5rem' }}>{s.quantity}</td>
                            <td style={{ padding: '0.5rem' }}>{Number(s.rate).toFixed(2)}</td>
                            <td style={{ padding: '0.5rem' }}>{Number(s.amount).toFixed(2)}</td>
                            <td style={{ padding: '0.5rem' }}>{s.igst}%</td>
                            <td style={{ padding: '0.5rem', fontWeight: 600 }}>{Number(s.total_amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={5} style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 700 }}>Grand Total</td>
                          <td style={{ padding: '0.5rem', fontWeight: 700 }}>₹{Number(viewData.totals.grand_total).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Renewal;
